import { Screen } from '@fans-fund-me/shared-comm-react-cosmos/fans-fund-me/account-settings-screen';
import { LINKS } from '@fans-fund-me/shared-comm-react-cosmos/fans-fund-me/links';
import { useAuth } from '@fans-fund-me/shared-common-esm-authentication';
import { useCurrency } from '@fans-fund-me/shared-common-esm-currency';
import { useModalsStack } from '@mantine/core';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { login, updatePassword, updateUserDetails } from '../../api/authentication.ts';
import { deleteProfile, getProfile, updateProfile } from '../../api/profile.ts';
import { startLinkXAccount, unlinkXAccount } from '../../api/x-auth.ts';
import currencies from '../../constants/currencies.json';
import { FORM_ERRORS } from '../../constants/form_errors.ts';

function AccountSettings() {
  const auth = useAuth();
  const navigate = useNavigate();
  const currency = useCurrency();

  const modalStack = useModalsStack([
    'email-address',
    'change-password',
    'update-currency',
    'surprise-gift-minimum',
    'delete-account',
  ]);

  const [isLoading, setIsLoading] = useState(false);
  const [isErrored, setIsErrored] = useState(false);
  const [emailAddress, setEmailAddress] = useState('');
  const [profileCurrency, setProfileCurrency] = useState('');
  const [surpriseGiftMinimum, setSurpriseGiftMinimum] = useState('');
  const [emailProductUpdates, setEmailProductUpdates] = useState(false);
  const [emailNotificationsEnabled, setEmailNotificationsEnabled] = useState(false);
  const [enableSurpriseGifts, setEnableSurpriseGifts] = useState(false);
  const [enableAutomaticXPosts, setEnableAutomaticXPosts] = useState(false);

  const emailAddressForm = useForm({
    mode: 'uncontrolled',
    initialValues: {
      email_address: emailAddress,
    },
    validate: {
      email_address: (value) => (/^\S+@\S+$/.test(value) ? null : FORM_ERRORS.EMAIL_INVALID),
    },
  });

  const handleEmailAddressFormSubmit = (values: typeof emailAddressForm.values) => {
    updateUserDetails({ email: values.email_address }, auth.id, auth.token)
      .then(() => {
        // emailAddressForm.setValues({ email_address: values.email_address });
        // auth.setEmail(values.email_address);
        setEmailAddress(values.email_address);
        modalStack.close('email-address');
      })
      .catch((error) => {
        emailAddressForm.setErrors({
          email_address: error.response.data.fields?.find(
            (field: { fieldName: string; message: string }) => field.fieldName === 'email',
          )?.message,
        });
      });
  };

  const changePasswordForm = useForm({
    mode: 'uncontrolled',
    initialValues: {
      password: '',
      new_password: '',
      confirm_password: '',
    },
    validate: {
      password: (value) => (value.length < 8 ? FORM_ERRORS.PASSWORD_LENGTH_VALIDATION : null),
      new_password: (value) => (value.length < 8 ? FORM_ERRORS.PASSWORD_LENGTH_VALIDATION : null),
      confirm_password: (value, values) =>
        value !== values.new_password ? FORM_ERRORS.PASSWORD_CONFIRMATION_VALIDATION : null,
    },
  });

  const handleChangePasswordFormSubmit = (values: typeof changePasswordForm.values) => {
    updatePassword(
      {
        email: emailAddress,
        oldPassword: values.password,
        newPassword: values.new_password,
      },
      auth.token,
    )
      .then(() => {
        changePasswordForm.reset();
        modalStack.close('change-password');
      })
      .catch((error) => {
        changePasswordForm.setErrors({
          password: error.response.data.fields?.find(
            (field: { fieldName: string; message: string }) => field.fieldName === 'password',
          )?.message,
          new_password: error.response.data.fields?.find(
            (field: { fieldName: string; message: string }) => field.fieldName === 'newPassword',
          )?.message,
          confirm_password: error.response.data.fields?.find(
            (field: { fieldName: string; message: string }) => field.fieldName === 'confirmPassword',
          )?.message,
        });
      });
  };

  const updateCurrencyForm = useForm({
    mode: 'uncontrolled',
    initialValues: {
      currency: '',
    },
    validate: {
      currency: (value) => (value.length < 3 ? FORM_ERRORS.CURRENCY_REQUIRED : null),
    },
  });

  const handleUpdateCurrencyFormSubmit = (values: typeof updateCurrencyForm.values) => {
    updateProfile({ currency: values.currency }, auth.id, auth.token)
      .then(() => {
        updateCurrencyForm.reset();
        setProfileCurrency(values.currency);
        currency.setCurrency(values.currency);
        modalStack.close('update-currency');
      })
      .catch((error) => {
        updateCurrencyForm.setErrors({
          currency: error.response.data.fields?.find(
            (field: { fieldName: string; message: string }) => field.fieldName === 'currency',
          )?.message,
        });
      });
  };

  const surpriseGiftMinimumForm = useForm({
    mode: 'uncontrolled',
    initialValues: {
      amount: '',
    },
    validate: {
      amount: (value) => (isNaN(Number(value)) ? FORM_ERRORS.MUST_BE_NUMBER : null),
    },
  });

  const handleSurpriseGiftMinimumFormSubmit = (values: typeof surpriseGiftMinimumForm.values) => {
    updateProfile({ surpriseGiftsMinimum: values.amount }, auth.id, auth.token)
      .then((response) => {
        modalStack.close('surprise-gift-minimum');
        surpriseGiftMinimumForm.reset();
        setSurpriseGiftMinimum(response.data.surpriseGiftsMinimum);
      })
      .catch((error) => {
        surpriseGiftMinimumForm.setErrors({
          amount: error.response.data.fields?.find(
            (field: { fieldName: string; message: string }) => field.fieldName === 'surpriseGiftsMinimum',
          )?.message,
        });
      });
  };

  const deleteAccountForm = useForm({
    mode: 'uncontrolled',
    initialValues: {
      password: '',
    },
    validate: {
      password: (value) => (value.length < 8 ? FORM_ERRORS.PASSWORD_LENGTH_VALIDATION : null),
    },
  });

  const handleDeleteAccountFormSubmit = (values: typeof deleteAccountForm.values) => {
    login({ email: emailAddress, password: values.password })
      .then(() => {
        deleteProfile(auth.id, auth.token).then(() => {
          auth
            .logout()
            .then(() => navigate(LINKS.LOGIN))
            .catch(() => navigate(LINKS.LOGIN));
        });
      })
      .catch((error) => {
        deleteAccountForm.setErrors({
          password: error.response.data.fields?.find(
            (field: { fieldName: string; message: string }) => field.fieldName === 'password',
          )?.message,
        });
      });
  };

  const handleUpdateSurpriseGifts = (value: boolean) => {
    setEnableSurpriseGifts(value);
    updateProfile({ enableSurpriseGifts: value }, auth.id, auth.token).catch(() => {
      setEnableSurpriseGifts(!value);
      notifications.show({
        title: 'Update Failed',
        message: "It wasn't possible to update your preferences for Surprise Gifts. Please try again later.",
        autoClose: 7000,
        color: 'red',
      });
    });
  };

  const handleEnableAutomaticXPosts = () => {
    startLinkXAccount(auth.token, auth.id).then((response) => (window.location.href = response.data.url));
  };

  const handleDisableAutomaticXPosts = () => {
    unlinkXAccount(auth.token, auth.id).then(() => {
      setEnableAutomaticXPosts(false);
    });
  };

  const handleUpdateEmailProductUpdates = (value: boolean) => {
    setEmailProductUpdates(value);
    updateProfile({ emailProductUpdates: value }, auth.id, auth.token).catch(() => {
      setEmailProductUpdates(!value);
      notifications.show({
        title: 'Update Failed',
        message: "It wasn't possible to update your preferences for Product Update emails. Please try again later.",
        autoClose: 7000,
        color: 'red',
      });
    });
  };

  const handleUpdateEmailNotifications = (value: boolean) => {
    setEmailNotificationsEnabled(value);
    updateProfile({ emailGiftNotification: value }, auth.id, auth.token).catch(() => {
      setEmailNotificationsEnabled(!value);
      notifications.show({
        title: 'Update Failed',
        message: "It wasn't possible to update your preferences for Gift Notification emails. Please try again later.",
        autoClose: 7000,
        color: 'red',
      });
    });
  };

  const handleLogout = () => {
    auth.logout().then(() => {
      navigate(LINKS.LOGIN);
    });
  };

  const handleLogoutEverywhere = () => {
    auth.logoutEverywhere().then(() => {
      navigate(LINKS.LOGIN);
    });
  };

  useEffect(() => {
    setIsLoading(true);
    if (auth.token !== undefined && auth.token !== '' && auth.id !== undefined && auth.id !== '') {
      getProfile(auth.id, auth.token)
        .then((profile) => {
          if (profile.data.email === undefined) {
            auth.logout().then(() => {
              navigate(LINKS.LOGIN);
            });
          } else {
            setEmailAddress(profile.data.email);
            setEmailProductUpdates(profile.data.emailProductUpdates);
            setEmailNotificationsEnabled(profile.data.emailGiftNotification);
            setEnableSurpriseGifts(profile.data.enableSurpriseGifts);
            setEnableAutomaticXPosts(profile.data.enableAutomaticXPosts || false);
            setProfileCurrency(profile.data.currency);
            setSurpriseGiftMinimum(profile.data.surpriseGiftsMinimum || '');

            setIsLoading(false);
          }
        })
        .catch(() => {
          setIsErrored(true);
          setIsLoading(false);
        });
    }

    // getXAccountDetails(auth.token, auth.id).then((response) => {
    //   setEnableAutomaticXPosts(response.data.isEnabled);
    // });
  }, [auth.id, auth.token]);

  return (
    <Screen
      modalStack={modalStack}
      currencies={currencies}
      isLoading={isLoading}
      isErrored={isErrored}
      emailAddress={emailAddress}
      emailAddressForm={emailAddressForm}
      handleEmailAddressFormSubmit={handleEmailAddressFormSubmit}
      changePasswordForm={changePasswordForm}
      handleChangePasswordFormSubmit={handleChangePasswordFormSubmit}
      currency={profileCurrency}
      updateCurrencyForm={updateCurrencyForm}
      handleUpdateCurrencyFormSubmit={handleUpdateCurrencyFormSubmit}
      surpriseGiftMinimum={surpriseGiftMinimum}
      surpriseGiftMinimumForm={surpriseGiftMinimumForm}
      handleSurpriseGiftMinimumFormSubmit={handleSurpriseGiftMinimumFormSubmit}
      deleteAccountForm={deleteAccountForm}
      handleDeleteAccountFormSubmit={handleDeleteAccountFormSubmit}
      enableSurpriseGifts={enableSurpriseGifts}
      setEnableSurpriseGifts={handleUpdateSurpriseGifts}
      enableAutomaticTweeting={enableAutomaticXPosts}
      setEnableAutomaticTweeting={enableAutomaticXPosts ? handleDisableAutomaticXPosts : handleEnableAutomaticXPosts}
      emailProductUpdates={emailProductUpdates}
      setEmailProductUpdates={handleUpdateEmailProductUpdates}
      emailNotificationsEnabled={emailNotificationsEnabled}
      setEmailNotificationsEnabled={handleUpdateEmailNotifications}
      handleLogout={handleLogout}
      handleLogoutEverywhere={handleLogoutEverywhere}
    />
  );
}

export default AccountSettings;
